import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, CONTACT } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-paper/90 backdrop-blur-md border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <button onClick={() => go("#top")} data-testid="logo-home-button" aria-label="Til toppen">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => go(n.href)}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className="font-oswald uppercase text-sm tracking-widest text-ink/80 hover:text-redx transition-colors duration-200"
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:+47${CONTACT.phoneRaw}`}
            data-testid="header-phone-link"
            className="hidden sm:flex items-center gap-2 bg-ink text-white px-5 py-3 font-oswald uppercase text-sm tracking-widest hover:bg-redx transition-colors duration-200"
          >
            <Phone size={15} /> {CONTACT.phone}
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-ink p-2"
            data-testid="mobile-menu-toggle"
            aria-label="Meny"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-paper border-t border-ink/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => (
                <button
                  key={n.href}
                  onClick={() => go(n.href)}
                  data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                  className="text-left font-oswald uppercase text-2xl tracking-tight text-ink hover:text-redx transition-colors"
                >
                  {n.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
